// Confirmação de ação destrutiva.
//
// Substitui o `window.confirm`, que tem três defeitos sérios num produto: não se
// parece com o resto da interface, não diz QUAL item está prestes a sumir com a
// ênfase que o momento pede, e — o pior — trava a aba inteira enquanto a ação
// acontece, sem nenhum sinal de progresso. O cliente clica "OK", a tela congela,
// e ele não sabe se deu certo, se travou, ou se pode clicar de novo.
//
// Aqui o botão de confirmar assume o estado de carregando, o de cancelar sai de
// cena, e o diálogo se recusa a fechar no meio — fechar durante a chamada
// deixaria o cliente sem saber o desfecho.
import type { ReactNode } from 'react';
import { Loader2, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { useT } from '@/lib/i18n';

export function ConfirmDialog({
  open, onOpenChange, title, description, confirmLabel, onConfirm, busy = false,
  variant = 'destructive', children,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  /** Enquanto true: rótulo vira spinner, cancelar some e o diálogo não fecha. */
  busy?: boolean;
  variant?: 'destructive' | 'default';
  /** Detalhe opcional do alvo — o nome do que vai sumir, um aviso extra. */
  children?: ReactNode;
}) {
  const t = useT();
  return (
    <Dialog
      open={open}
      // Durante a chamada o diálogo fica: fechar aqui esconderia o desfecho.
      onOpenChange={(v) => { if (!busy) onOpenChange(v); }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {variant === 'destructive' && <TriangleAlert className="size-4 text-destructive" />}
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {children}

        <DialogFooter>
          {!busy && (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
          )}
          <Button variant={variant} disabled={busy} onClick={onConfirm}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            {busy ? t('common.working') : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
